import type {
    FC
} from "react";
import {
    Pressable,
    Animated,
    View
} from "react-native";
import type ICarouselControlProps from "./type";
import stylesheet, {
    getCarouselControlColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePressScale
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    getControlMeasures
} from "../../../../helpers/theme/control";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const CarouselControl: FC<ICarouselControlProps> = ({
    isSignatureEnabled = false,
    isTransitioned = false,
    isDisabled = false,
    isOverlaid = false,
    borderRadius,
    customTheme,
    fromIcon,
    onPress,
    toIcon,
    label,
    style,
    size,
    icon
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const currentSize = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const currentColors = getCarouselControlColors({
        isDisabled,
        isOverlaid,
        colors
    });

    const currentBorderRadius = borderRadius ?? radiuses.actions;

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        borderRadius: currentBorderRadius,
        isFocusVisible,
        currentColors,
        touchTargets,
        shapeConfigs,
        currentSize,
        pointerType,
        shadowAlpha,
        elevations,
        isDisabled,
        borders,
        colors
    });

    const pressScaleStyle = usePressScale({
        isPressed: isPressed && !isDisabled,
        customTheme,
        isDisabled
    });

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        onPress();
    };

    return <AnimatedPressable
        {...interactionHandlers}
        style={[
            style,
            stylesheet.container,
            isDisabled ? stylesheet.disabled : undefined,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        aria-disabled={isDisabled}
        onPress={handlePress}
        disabled={isDisabled}
        aria-label={label}
        role="button"
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                overlayColor={currentColors.overlayColor}
                isHovered={!isDisabled && isHovered}
                isPressed={!isDisabled && isPressed}
                borderRadius={currentBorderRadius}
                borderWidth={borders.line}
                customTheme={customTheme}
            />
            <AnimatedIcon
                animationTrigger={isSignatureEnabled ? "hover" : undefined}
                animation={isSignatureEnabled ? "signature" : undefined}
                customColor={currentColors.iconColor}
                isTransitioned={isTransitioned}
                size={currentSize.iconSize}
                customTheme={customTheme}
                fromIcon={fromIcon}
                toIcon={toIcon}
                icon={icon}
            />
        </View>
    </AnimatedPressable>;
};
export default CarouselControl;
